/**
 * AUTHENTICATION SERVICE
 * Handles login, registration, and token refresh
 */

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    username: string;
    balance: number;
  };
}

// Map backend errors to user-friendly messages
function getErrorMessage(error: any): string {
  const message = error?.message || error?.error || "";

  // Handle specific error messages
  if (message.includes("Invalid credentials")) {
    return "Username or password is incorrect";
  }
  if (message.includes("Username already taken")) {
    return "This username is already taken. Please choose another.";
  }
  if (message.includes("must be at least 8 characters")) {
    return "Password must be at least 8 characters long";
  }
  if (message.includes("Username must")) {
    return "Username must be 3-20 characters with letters, numbers, or underscores";
  }
  if (message.includes("banned")) {
    return "This account has been suspended";
  }
  if (message.includes("inactive")) {
    return "This account is inactive";
  }

  return "Authentication failed. Please try again.";
}

export async function login(
  username: string,
  password: string,
): Promise<AuthResponse> {
  const response = await fetch(`${API_URL}/api/auth/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: response.statusText }));
    throw new Error(getErrorMessage(error));
  }

  return response.json();
}

export async function register(
  username: string,
  password: string,
): Promise<AuthResponse> {
  const response = await fetch(`${API_URL}/api/auth/register`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: response.statusText }));
    throw new Error(getErrorMessage(error));
  }

  return response.json();
}

export async function refreshToken(
  refreshToken: string,
): Promise<AuthResponse> {
  const response = await fetch(`${API_URL}/api/auth/refresh`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    throw new Error("Session expired. Please login again.");
  }

  return response.json();
}
